import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from './home/Navbar';
import Footer from './Footer';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';
import GuideNotification from './GuideNotification';

function StartedGuide() {
  const { t } = useTranslation();
  const { events } = useGoogleAnalytics();
  const certItems = t('started_guide.section1.items', { returnObjects: true });
  const resources = t('started_guide.section2.resources', { returnObjects: true });
  const glossaryItems = t('started_guide.glossary.items', { returnObjects: true });

  const plansComparisonTitle = t('started_guide.plans_comparison.title');
  const plansComparisonMoreInfo = t('started_guide.plans_comparison.more_info');
  const plansComparisonLinkText = t('started_guide.plans_comparison.link_text');

  const table1Title = t('started_guide.plans_comparison.table1.title');
  const table1Headers = t('started_guide.plans_comparison.table1.headers', { returnObjects: true }) || [];
  const table1Rows = t('started_guide.plans_comparison.table1.rows', { returnObjects: true }) || [];

  const table2Title = t('started_guide.plans_comparison.table2.title');
  const table2Headers = t('started_guide.plans_comparison.table2.headers', { returnObjects: true }) || [];
  const table2Rows = t('started_guide.plans_comparison.table2.rows', { returnObjects: true }) || [];

  const table1HeadersArray = Array.isArray(table1Headers) ? table1Headers : [];
  const table1RowsArray = Array.isArray(table1Rows) ? table1Rows : [];
  const table2HeadersArray = Array.isArray(table2Headers) ? table2Headers : [];
  const table2RowsArray = Array.isArray(table2Rows) ? table2Rows : [];

  useEffect(() => {
    window.scrollTo(0, 0);
    events.pageView('started_guide');
  }, [events]);

  return (
    <div className="flex flex-col min-h-screen bg-ink font-sans text-white antialiased">
      <Navbar />
      <div className="h-16" />
      <GuideNotification />
      
      <main className="flex-grow">
        {/* Section 1: Certification Path */}
        <section className="pt-16 md:pt-24 pb-12 bg-ink">
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
                {t('started_guide.section1.title')}
              </h1>
              <p className="text-xl text-white/60">
                {t('started_guide.section1.subtitle')}
              </p>
            </div>

            <div className="relative">
              {/* Horizontal Path Container */}
              <div className="overflow-x-auto pb-8 pt-4 no-scrollbar">
                <div className="flex items-start gap-6 min-w-max lg:min-w-0 lg:justify-between px-4 relative">
                  {/* Connecting Line (Desktop) */}
                  <div className="absolute top-6 left-0 right-0 h-1 bg-accent/20 hidden lg:block z-0"></div>

                  {certItems.map((item, index) => (
                    <div key={index} className="flex flex-col items-center w-48 text-center relative z-10">
                      {/* Step Number Circle */}
                      <div className="w-12 h-12 rounded-full bg-accent border-4 border-ink shadow-lg flex items-center justify-center text-white font-bold mb-4 transition-transform hover:scale-110 duration-300">
                        {index + 1}
                      </div>
                      
                      {/* Item Content Card */}
                      <div className="bg-elevated p-5 rounded-xl border border-line w-full min-h-[120px] flex items-center justify-center hover:border-accent/40 transition-all duration-300">
                        <p className="text-white font-bold text-sm leading-tight">
                          {item}
                        </p>
                      </div>

                      {/* Small arrow/indicator for mobile between items */}
                      {index < certItems.length - 1 && (
                        <div className="lg:hidden absolute -right-4 top-6 text-accent/40">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 text-center">
                <a 
                  href="https://training.zendesk.com/page/zendesk-certifications" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => events.clickResource('zendesk_certifications', 'https://training.zendesk.com/page/zendesk-certifications')}
                  className="inline-flex items-center px-8 py-3 bg-cta text-ink font-bold rounded-full hover:bg-cta-hover transition-all duration-300 shadow-sm hover:shadow-lg"
                >
                  {t('started_guide.section1.go_to_exams')}
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Resources List */}
        <section className="pt-8 pb-16 md:pb-24 bg-surface border-b border-line">
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center max-w-4xl mx-auto leading-tight">
              {t('started_guide.section2.title')}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.map((resource, index) => (
                <a 
                  key={index}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => events.clickResource(resource.label, resource.url)}
                  className="group p-6 rounded-xl border border-line bg-elevated hover:border-accent/40 transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="flex items-start">
                    <div className="p-3 rounded-lg bg-accent/10 group-hover:bg-accent/20 mr-4 transition-colors">
                      <svg className="w-6 h-6 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white/80 group-hover:text-white font-medium text-lg leading-snug">
                        {resource.label}
                      </p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Section 2.5: The Zendesk Glossary */}
        <section className="py-16 bg-ink">
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
                {t('started_guide.glossary.title')}
              </h2>
              <div className="w-20 h-1.5 bg-accent mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {glossaryItems.map((item, index) => (
                <div 
                  key={index} 
                  className="bg-elevated p-6 rounded-2xl border border-line hover:border-accent/30 transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-accent/15 text-accent-400 rounded-lg flex items-center justify-center font-bold text-lg group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-accent-400 transition-colors">
                        {item.term}
                      </h3>
                      <p className="text-white/60 leading-relaxed text-lg">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* New Section: Which Zendesk Plan should I use? */}
        <section className="py-16 bg-surface text-white">
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
                {plansComparisonTitle}
              </h2>
              <div className="w-20 h-1.5 bg-accent mx-auto rounded-full"></div>
            </div>

            {/* Table 1: Recommendation by Company Size */}
            <div className="mb-16">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-6">
                {table1Title}
              </h3>
              <div className="overflow-x-auto rounded-xl border border-line">
                <table className="w-full text-left border-collapse min-w-[800px]">
                  <thead>
                    <tr className="border-b border-line bg-elevated">
                      {table1HeadersArray.map((header, idx) => (
                        <th key={idx} className="py-4 px-6 font-bold text-xs uppercase tracking-wider text-white/70">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line bg-ink">
                    {table1RowsArray.map((row, idx) => (
                      <tr key={idx} className="hover:bg-white/5 transition-colors duration-150">
                        <td className="py-4 px-6 text-sm text-white font-semibold">{row.size}</td>
                        <td className="py-4 px-6 text-sm text-accent-400 font-bold">{row.plan}</td>
                        <td className="py-4 px-6 text-sm text-white/60 leading-relaxed">{row.fits}</td>
                        <td className="py-4 px-6 text-sm text-white/40 leading-relaxed">{row.not_fits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Table 2: Recommendation by Business Need */}
            <div className="mb-12">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-6">
                {table2Title}
              </h3>
              <div className="overflow-x-auto rounded-xl border border-line">
                <table className="w-full text-left border-collapse min-w-[800px]">
                  <thead>
                    <tr className="border-b border-line bg-elevated">
                      {table2HeadersArray.map((header, idx) => (
                        <th key={idx} className="py-4 px-6 font-bold text-xs uppercase tracking-wider text-white/70">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line bg-ink">
                    {table2RowsArray.map((row, idx) => (
                      <tr key={idx} className="hover:bg-white/5 transition-colors duration-150">
                        <td className="py-4 px-6 text-sm text-white/70 leading-relaxed">{row.want}</td>
                        <td className="py-4 px-6 text-sm text-accent-400 font-bold">{row.plan}</td>
                        <td className="py-4 px-6 text-sm text-white/60 leading-relaxed">{row.why}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* More Info Link */}
            <div className="text-center mt-12 bg-elevated p-6 rounded-2xl border border-line max-w-3xl mx-auto">
              <p className="text-lg text-white/60 flex items-center justify-center flex-wrap gap-1">
                <span>{plansComparisonMoreInfo}</span>
                <a
                  href="https://www.zendesk.com/pricing/#modal.pricing-table"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => events.clickResource('zendesk_pricing_table', 'https://www.zendesk.com/pricing/#modal.pricing-table')}
                  className="text-accent-400 hover:text-white font-bold underline inline-flex items-center gap-1 group transition-colors"
                >
                  {plansComparisonLinkText}
                  <svg className="w-4 h-4 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Consultation CTA */}
        <section className="py-20 bg-ink border-t border-line text-white">
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('started_guide.section3.title')}
            </h2>
            <p className="text-xl text-white/60 mb-10 max-w-3xl mx-auto">
              {t('started_guide.section3.subtitle')}
            </p>
            <a 
              href="https://calendar.app.google/URR5NVNKa7FChwa76"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => events.scheduleConsultation('started_guide')}
              className="inline-block bg-cta hover:bg-cta-hover text-ink text-xl font-bold py-4 px-10 rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              {t('started_guide.section3.cta')}
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default StartedGuide;
